import { useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FormInput } from './FormControls.jsx';

const DEFAULT_ROUTING_RULES = '{\n  "default_collection": "uploads",\n  "mime_prefixes": {\n    "image/": "images",\n    "video/": "video"\n  }\n}';

export function OrganizationIntegrationsSettings() {
  const [org, setOrg] = useState(null);
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState('');
  const [routingRules, setRoutingRules] = useState(DEFAULT_ROUTING_RULES);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiJson('/api/admin/organization')
      .then((o) => {
        setOrg(o);
        setTeamsWebhookUrl(o.teams_webhook_url || '');
        if (o.asset_routing_rules) {
          setRoutingRules(JSON.stringify(o.asset_routing_rules, null, 2));
        }
      })
      .catch((e) => setError(e.message));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMsg('');
    try {
      let asset_routing_rules = null;
      if (routingRules.trim()) {
        asset_routing_rules = JSON.parse(routingRules);
      }
      const updated = await apiJson('/api/admin/organization', {
        method: 'PUT',
        body: { teams_webhook_url: teamsWebhookUrl, asset_routing_rules },
      });
      setOrg(updated);
      setMsg('Saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const regenerateWebhook = async () => {
    setError('');
    setMsg('');
    try {
      const updated = await apiJson('/api/admin/organization', {
        method: 'PUT',
        body: { regenerate_ghl_webhook_secret: true },
      });
      setOrg(updated);
      setMsg('GHL webhook secret regenerated.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!org && !error) {
    return <p className="muted">Loading organization settings…</p>;
  }

  const webhookUrl = org?.id ? `/api/webhooks/ghl/${org.id}` : '';

  return (
    <div data-tour="integrations-webhooks">
      {error ? <p className="err">{error}</p> : null}
      {msg ? <p className="muted">{msg}</p> : null}

      <form onSubmit={save} className="card-panel stack" style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>Microsoft Teams notifications</h3>
        <p className="muted small" style={{ margin: 0 }}>
          Incoming webhook for task and portal alerts in Teams.
        </p>
        <label className="field">
          <span>Microsoft Teams incoming webhook URL</span>
          <FormInput
            value={teamsWebhookUrl}
            onChange={(e) => setTeamsWebhookUrl(e.target.value)}
            placeholder="https://outlook.office.com/webhook/…"
          />
        </label>

        <label className="field" data-tour="integrations-routing">
          <span>Asset upload routing (JSON)</span>
          <textarea
            rows={8}
            value={routingRules}
            onChange={(e) => setRoutingRules(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
          <span className="muted small">
            MIME prefix → collection slug. Client uploads auto-route when no collection is chosen.
          </span>
        </label>

        <div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>

      <div className="card-panel">
        <h3 style={{ marginTop: 0 }}>GHL webhook</h3>
        <p className="muted small">POST opportunity updates to (API base):</p>
        <code style={{ wordBreak: 'break-all' }}>{webhookUrl}</code>
        <p className="muted small">Header: X-Portal-Webhook-Secret</p>
        {org?.ghl_webhook_secret ? (
          <p>
            <strong>Secret:</strong> <code>{org.ghl_webhook_secret}</code>
          </p>
        ) : (
          <p className="muted">No secret set — regenerate to enable verification.</p>
        )}
        <button type="button" className="btn-secondary" onClick={() => void regenerateWebhook()}>
          Regenerate webhook secret
        </button>
      </div>
    </div>
  );
}
